import firebaseApp from "firebase/compat/app";

type DataSourceItem = {
  id: string;
  title: string;
};

type DataField = {
  __example__: string;
  type: "string";
};

export type NavigateAction = {
  name: "navigate";
  next: {
    name: string;
    type: "screen";
  };
  payload: Record<string, string>;
};

type CompleteAction = {
  name: "complete";
  payload: Record<string, string>;
};

type OnClickAction = NavigateAction | CompleteAction;

type BaseFormElement = {
  type: string;
  label?: string;
  name?: string;
  required?: boolean;
};

export type TextHeading = {
  type: "TextHeading";
  text: string;
};

export type TextSubheading = {
  type: "TextSubheading";
  text: string;
};

export type TextCaption = {
  type: "TextCaption";
  text: string;
};

export type TextBody = {
  type: "TextBody";
  text: string;
};

export type Image = {
  type: "Image";
  src: string;
  width?: number;
  height?: number;
  "scale-type": "contain" | "cover";
  "aspect-ratio": "1"; // default
};

export type TextInput = BaseFormElement & {
  type: "TextInput";
  "input-type": "text";
  label: string;
  name: string;
  required: boolean;
};

export type TextArea = BaseFormElement & {
  type: "TextArea";
  label: string;
  name: string;
  required: boolean;
};

export type DatePicker = BaseFormElement & {
  type: "DatePicker";
  label: string;
  name: string;
  required: boolean;
};

export type RadioButtonsGroup = BaseFormElement & {
  type: "RadioButtonsGroup";
  label: string;
  name: string;
  "data-source": DataSourceItem[];
  required: boolean;
};

export type CheckboxGroup = BaseFormElement & {
  type: "CheckboxGroup";
  label: string;
  name: string;
  "data-source": DataSourceItem[];
  required: boolean;
};

export type Dropdown = BaseFormElement & {
  type: "Dropdown";
  label: string;
  name: string;
  "data-source": DataSourceItem[];
  required: boolean;
};

export type OptIn = BaseFormElement & {
  type: "OptIn";
  label: string;
  name: string;
  required: boolean;
};

export type Footer = BaseFormElement & {
  type: "Footer";
  label: string;
  "on-click-action": OnClickAction;
};

export type FormElementText =
  | TextHeading
  | TextSubheading
  | TextCaption
  | TextBody;

export const formElementTextLengthMap: Record<FormElementText["type"], number> =
  {
    TextHeading: 80,
    TextSubheading: 80,
    TextCaption: 4096,
    TextBody: 4096,
  };

export type FormElementInput =
  | TextInput
  | TextArea
  | DatePicker
  | RadioButtonsGroup
  | CheckboxGroup
  | Dropdown
  | OptIn;

export const formElementInputLengthMap: Record<
  FormElementInput["type"],
  number
> = {
  TextInput: 20,
  TextArea: 20,
  DatePicker: 20,
  RadioButtonsGroup: 30,
  CheckboxGroup: 30,
  Dropdown: 20,
  OptIn: 300,
};

export type FormElementDropdown = RadioButtonsGroup | CheckboxGroup | Dropdown;

export type FormElement =
  | TextHeading
  | TextSubheading
  | TextCaption
  | TextBody
  | Image
  | TextInput
  | TextArea
  | DatePicker
  | RadioButtonsGroup
  | CheckboxGroup
  | Dropdown
  | OptIn
  | Footer;

type Form = {
  type: "Form";
  name: string;
  "init-values"?: string;
  children: FormElement[];
};

type SingleColumnLayout = {
  type: "SingleColumnLayout";
  children: Form[];
};

export type Screen = {
  id: string; // should only consist of alphabets and underscores.
  title: string;
  terminal?: boolean; // Indicates if this is the last screen in the flow
  layout: SingleColumnLayout; // Right now, only SingleColumnLayout is supported
  data?: Record<string, DataField | Record<string, unknown>>;
};

export type WhatsappFlow = {
  id?: string;
  name: string;
  type?: "standard" | "customized";
  categories: string[];
  userId: string;
  flow: {
    version: string; // default is "7.0"
    screens: Screen[];
  };
  createdAt: Date | firebaseApp.firestore.FieldValue;
  updatedAt: Date | firebaseApp.firestore.FieldValue;
  deprecatedAt?: Date | firebaseApp.firestore.FieldValue;
  metaId?: string;
  completionMessage?: string;
  integration?: "google_sheet" | "email";
  integrationConfig?: Record<string, unknown>;
  saveFormInContact?: boolean;
  status: "draft" | "published" | "deprecated";
};

export const whatsappFlowStatusMap: Record<WhatsappFlow["status"], string> = {
  draft: "Draft",
  published: "Published",
  deprecated: "Deprecated",
};

export const whatsappFlowStatusColorMap: Record<
  WhatsappFlow["status"],
  string
> = {
  draft: "#F4CA49",
  published: "#27AE60",
  deprecated: "#E74C3C",
};
